import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthzProvider as SharedAuthzProvider } from '@ohpen/react-authz';
import { useAuth } from '@ohpen/react-auth';
import { PmpConfigContext } from '@pmp/contexts';
import { AuthzContextType, FeatureFlag, Permission, Role } from './interfaces';
import { getUserGroups, permissionByRole } from './utils';

export function AuthzProvider({ children }: { readonly children: React.ReactNode }) {
  const { featureToggles } = useContext(PmpConfigContext);
  const auth = useAuth();
  const [userPermissions, setUserPermissions] = useState<Set<Permission>>(new Set());
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const customGroups = auth.user?.attributes?.['custom:groups'];

  const envFeatureFlags = useMemo(() => {
    const on = Object.entries(featureToggles ?? {})
      .filter(([, v]) => Boolean(v))
      .map(([k]) => k as FeatureFlag);
    return new Set<FeatureFlag>(on);
  }, [featureToggles]);

  const isLoading = useMemo(
    () => auth.isLoading || !permissionsLoaded || featureToggles === undefined,
    [auth.isLoading, permissionsLoaded, featureToggles],
  );

  useEffect(() => {
    // Wait until auth is fully resolved before deriving permissions.
    // Otherwise the effect fires with customGroups=undefined while auth is still
    // loading, sets an empty permission set, and briefly shows a 404.
    if (auth.isLoading) return;

    const loadUserPermissions = async () => {
      if (customGroups) {
        const userRoles = getUserGroups(customGroups);
        const perms = userRoles.flatMap(role => permissionByRole[role as Role] ?? []);
        const permsSet = new Set<Permission>(perms);
        setUserPermissions(permsSet);
      } else {
        setUserPermissions(new Set());
      }

      setPermissionsLoaded(true);
    };

    loadUserPermissions();
  }, [auth.isLoading, customGroups]);

  const value: AuthzContextType = useMemo(
    () => ({ userPermissions, envFeatureFlags, isLoading }),
    [userPermissions, envFeatureFlags, isLoading],
  );

  return <SharedAuthzProvider value={value}>{children as any}</SharedAuthzProvider>;
}
